import { useState } from "react";
import { Link } from "wouter";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseNumber } from "./numberInput";
import { fmtMoney } from "./money";
import { paycheck, hourlyToAnnual, PAY_PERIODS } from "./paycheck";
import { FILING_LABEL, US_TAX_YEAR, type Filing } from "./usTax";
import { STATES } from "./stateTaxData";
import { amountError } from "./calcChecks";

const TOOL_INFO_CONTENT = {
  what: `Estimates your US take-home pay per paycheck after federal income tax (${US_TAX_YEAR} brackets for your filing status), Social Security, Medicare, state tax and pre-tax deductions, from a yearly salary or an hourly rate with overtime.`,
  how: `Enter your salary (or hourly rate and hours), choose filing status, state and pay frequency, then add pre-tax deductions: a 401(k) percentage, and health premiums or HSA/FSA contributions. The result shows your net pay per check and a full breakdown.`,
  tip: `Traditional 401(k) contributions lower your income tax but are still subject to Social Security and Medicare tax; health premiums and HSA/FSA through payroll lower both. Your real paycheck also depends on your W-4, local taxes and employer-specific deductions.`,
};
const SEL = "border rounded px-2 py-2 w-full bg-background text-sm";
const usd = (n: number, d = 2) => fmtMoney(n, "USD", d);

export default function PaycheckCalculator() {
  const [basis, setBasis] = useState<"salary" | "hourly">("salary");
  const [salary, setSalary] = useState("75000");
  const [rate, setRate] = useState("25");
  const [hours, setHours] = useState("40");
  const [filing, setFiling] = useState<Filing>("single");
  const [state, setState] = useState("CUSTOM");
  const [statePct, setStatePct] = useState("0");
  const [frequency, setFrequency] = useState("biweekly");
  const [k401, setK401] = useState("0");
  const [health, setHealth] = useState("0");
  const [hsa, setHsa] = useState("0");

  const periods = PAY_PERIODS[frequency] ?? 26;
  const gross = basis === "salary" ? Math.max(0, parseNumber(salary) ?? 0) : hourlyToAnnual(Math.max(0, parseNumber(rate) ?? 0), Math.max(0, parseNumber(hours) ?? 0));
  const r = paycheck({
    gross, filing, state, customStatePct: parseNumber(statePct) ?? 0, retirementPct: Math.min(100, Math.max(0, parseNumber(k401) ?? 0)),
    section125Annual: Math.max(0, parseNumber(health) ?? 0) * periods + Math.max(0, parseNumber(hsa) ?? 0), periods,
  });
  const inputErr = basis === "salary" ? amountError(salary, "an annual salary")
    : amountError(rate, "an hourly rate") || amountError(hours, "hours per week");
  const { blocked } = useLogToolUsage(!inputErr, 3000);
  const Box = ({ label, v, id }: { label: string; v: number; id: string }) => (
    <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">{label}</p><p className="text-sm font-bold" data-testid={id}>{usd(v, 0)}</p></div>
  );
  const labelFreq = { weekly: "weekly", biweekly: "bi-weekly", semimonthly: "semi-monthly", monthly: "monthly" }[frequency];

  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <Tabs value={basis} onValueChange={(v) => setBasis(v as "salary" | "hourly")}>
          <TabsList className="w-full"><TabsTrigger value="salary" className="flex-1">Salary</TabsTrigger><TabsTrigger value="hourly" className="flex-1">Hourly</TabsTrigger></TabsList>
        </Tabs>
        {basis === "salary" ? (
          <div><Label>Annual Gross Salary ($)</Label><Input inputMode="decimal" aria-label="Annual salary" aria-invalid={!!inputErr} value={salary} onChange={(e) => setSalary(e.target.value)} /></div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Hourly rate ($)</Label><Input inputMode="decimal" aria-label="Hourly rate" aria-invalid={!!inputErr} value={rate} onChange={(e) => setRate(e.target.value)} /></div>
            <div><Label>Hours per week</Label><Input inputMode="decimal" aria-label="Hours per week" value={hours} onChange={(e) => setHours(e.target.value)} /></div>
            <p className="col-span-2 text-xs text-muted-foreground">Hours over 40 a week are paid at 1.5×. That is about {usd(gross, 0)} a year.</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Filing status</Label>
            <select className={SEL} aria-label="Filing status" value={filing} onChange={(e) => setFiling(e.target.value as Filing)}>
              {(Object.keys(FILING_LABEL) as Filing[]).map((k) => <option key={k} value={k}>{FILING_LABEL[k]}</option>)}
            </select></div>
          <div><Label className="text-xs">Pay frequency</Label>
            <select className={SEL} aria-label="Pay frequency" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              <option value="weekly">Weekly</option><option value="biweekly">Bi-weekly</option><option value="semimonthly">Semi-monthly</option><option value="monthly">Monthly</option>
            </select></div>
          <div><Label className="text-xs">State</Label>
            <select className={SEL} aria-label="State" value={state} onChange={(e) => setState(e.target.value)}>
              <option value="CUSTOM">Custom rate…</option>
              {STATES.map((s) => <option key={s.code} value={s.code}>{s.name}{s.kind === "none" ? " (no tax)" : ""}</option>)}
            </select></div>
          {state === "CUSTOM" && <div><Label className="text-xs">State tax rate (%)</Label><Input inputMode="decimal" aria-label="State tax rate" value={statePct} onChange={(e) => setStatePct(e.target.value)} /></div>}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">401(k) %</Label><Input inputMode="decimal" aria-label="401k percent" value={k401} onChange={(e) => setK401(e.target.value)} /></div>
          <div><Label className="text-xs">Health premium / check</Label><Input inputMode="decimal" aria-label="Health premium" value={health} onChange={(e) => setHealth(e.target.value)} /></div>
          <div><Label className="text-xs">HSA/FSA / year</Label><Input inputMode="decimal" aria-label="HSA" value={hsa} onChange={(e) => setHsa(e.target.value)} /></div>
        </div>

        {inputErr ? (
          <p role="alert" className="text-xs text-destructive" data-testid="pc-error">{inputErr}</p>
        ) : blocked ? (
          <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p>
        ) : (
          <>
            <div className="p-4 rounded-xl border bg-primary/5 text-center">
              <p className="text-xs text-muted-foreground">Estimated Take-Home Pay ({labelFreq})</p>
              <p className="text-2xl font-bold text-primary" data-testid="pc-net">{usd(r.netPerPeriod)}</p>
              <p className="text-xs text-muted-foreground mt-1">of {usd(r.grossPerPeriod)} gross · {usd(r.netAnnual, 0)} a year</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Box label="Federal income tax (yr)" v={r.fed} id="pc-fed" />
              <Box label="State tax (yr)" v={r.state} id="pc-state" />
              {r.statePayroll > 0 && <Box label={`${r.statePayrollLabel} (yr)`} v={r.statePayroll} id="pc-sdi" />}
              <Box label="Social Security (yr)" v={r.socialSecurity} id="pc-ss" />
              <Box label={r.additionalMedicare > 0 ? "Medicare + 0.9% extra (yr)" : "Medicare (yr)"} v={r.medicare + r.additionalMedicare} id="pc-med" />
              {r.retirement > 0 && <Box label="401(k) (yr)" v={r.retirement} id="pc-401k" />}
              {r.section125 > 0 && <Box label="Health / HSA (yr)" v={r.section125} id="pc-125" />}
            </div>
          </>
        )}
        <p className="text-xs text-muted-foreground text-center">
          Estimate using {US_TAX_YEAR} federal brackets and the standard deduction for your filing status. State figures are estimates. California SDI (1.3% of all wages) is included as its own line; the disability and paid-leave contributions of NJ, NY, HI, RI, WA and MA, and city or local income taxes (for example New York City) are not included, so take-home pay in those places will be lower than shown. Not tax advice — withholding depends on your W-4.
          See also the <Link href="/tool/income-tax-calculator" className="underline text-primary">Income Tax Calculator</Link>.
        </p>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
